import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

import { useAppSelector } from '../../../app/store';
import reducerPath from '../../../app/reducerPath';
import useShowError from '../../../shared/lib/hooks/useShowError';
import {
  useCreateVehicleRoutesMutation,
  useReorderLogisticTaskMutation,
} from '../../../entities/logistic/logisticTasks/redux/logisticTasks.api';
import {
  ROUTE_NAME_TPLS,
  logisticTasksPetrolResourceActions,
  transformData,
} from '../../../entities/logistic/logisticTasks/redux/logisticTasksPetrol.slice';
import CreateRoutesDialog from './components/CreateRoutesDialog/CreateRoutesDialog';
import cancelEvent from './utils/cancelEvent';
import MainPanelContent from './components/MainPanelContent/MainPanelContent';
import parseCoords from './utils/parseCoords';
import BackdropWrapper from './styles/BackdropWrapper';

export default function PetrolDeliveryMainPanelBll({
  logisticTask,
  patchLogisticTask,
  solveLogisticTask,
  resultPatchLogisticTask,
  startPolling,
  stopPolling,
  taskStatus,
  stopLogisticTask,
}) {
  const {
    data,
    data: { name: taskName },
    routeNameTemplate,
    rePlanDate,
    editedCoordinates,
    selectedVehicles,
  } = useAppSelector((state) => state[`${reducerPath.logisticTasksPetrolResource}/counter`]);
  const showError = useShowError();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [stopTaskTrigger] = stopLogisticTask;

  const [isRunning, setIsRunning] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [waitForStatusFlag, setWaitForStatusFlag] = useState(false);

  const [reorderLogisticTask, reorderLogisticTaskResult] = useReorderLogisticTaskMutation();

  const [createVehicleRoutes, createVehicleRoutesResult] = useCreateVehicleRoutesMutation();

  useEffect(() => {
    dispatch(logisticTasksPetrolResourceActions.setRePlanDate(logisticTask?.data?.source.routeStartDate || null));
  }, [logisticTask]);

  useEffect(() => {
    switch (taskStatus.status) {
      case 'RUNNING':
        setIsRunning(true);
        setIsStopping(false);
        startPolling();
        break;
      case 'STOPPING':
        setIsRunning(false);
        setIsStopping(true);
        startPolling();
        break;
      case 'SOLVED':
        setIsRunning(false);
        setIsStopping(false);
        stopPolling();
        logisticTask.refetch();
        break;
      default:
        setIsRunning(false);
        setIsStopping(false);
        stopPolling();
    }
    setWaitForStatusFlag(false);
  }, [taskStatus]);

  useEffect(() => {
    if (createVehicleRoutesResult.status === 'fulfilled') {
      navigate(`/companies/${logisticTask.data.companyId}/routes`);
    }
  }, [createVehicleRoutesResult.status]);

  useEffect(() => {
    if (reorderLogisticTaskResult.status === 'fulfilled') {
      const { data: payload } = reorderLogisticTaskResult;
      const dataWithMissingRoutes = {
        ...payload,
        solution: {
          ...logisticTask.data.solution,
          // eslint-disable-next-line react/prop-types
          routes: logisticTask.data.solution.routes.map((r) => {
            const newRoute = payload.solution.routes.find(
              (route) => route.tanker.registrationNum === r.tanker.registrationNum,
            );
            return newRoute || r;
          }),
        },
      };
      const newData = transformData(dataWithMissingRoutes);
      dispatch(logisticTasksPetrolResourceActions.setData({ data: newData }));
    }
  }, [reorderLogisticTaskResult.status]);

  const [isOpenCreateVehicleRouteDialog, setIsOpenCreateVehicleRouteDialog] = React.useState(false);

  const onCLickSave = (status = null) => {
    const orders = [...JSON.parse(JSON.stringify(data.source.orderSourceList))];
    const points = JSON.parse(JSON.stringify(data.sourceWithSolution.points));
    const keys = Object.keys(points);

    // Перебираем измененные коодинаты точек заказов, сохраняем в источник
    const orderSourceList = keys.reduce((prev, key) => {
      const point = points[key];
      const newPrev = [...prev];
      const index = orders.findIndex((order) => order.requestId === point.orderNumber);
      if (point.isEdited) {
        const coords = parseCoords(point.coordinates);
        if (point.isLoading) {
          newPrev[index] = {
            ...newPrev[index],
            loadingLon: coords?.lon,
            loadingLat: coords?.lat,
          };
        }
        if (point.isUnloading) {
          newPrev[index] = {
            ...newPrev[index],
            deliveryLon: coords?.lon,
            deliveryLat: coords?.lat,
          };
        }
      }
      const foundCoord = editedCoordinates[point.orderNumber];
      if (foundCoord) {
        if (point.isLoading && foundCoord.addressLoading) {
          newPrev[index] = {
            ...newPrev[index],
            loadingLon: foundCoord.addressLoading.lon,
            loadingLat: foundCoord.addressLoading.lat,
            loadingAddress: foundCoord.addressLoading.sourceAddress,
            addresses: {
              ...newPrev[index].addresses,
              loadingAddress: foundCoord.addressLoading.sourceAddress,
            },
          };
        }
        if (point.isUnloading && foundCoord.addressUnloading) {
          newPrev[index] = {
            ...newPrev[index],
            deliveryLon: foundCoord.addressUnloading.lon,
            deliveryLat: foundCoord.addressUnloading.lat,
            deliveryAddress: foundCoord.addressUnloading.sourceAddress,
            addresses: {
              ...newPrev[index].addresses,
              deliveryAddress: foundCoord.addressUnloading.sourceAddress,
            },
          };
        }
      }
      return newPrev;
    }, orders);
    const body = {
      name: data?.name || '',
      logisticTaskId: logisticTask.data.logisticTaskId,
      source: {
        ...data.source,
        orderSourceList,
        routeStartDate: rePlanDate || null,
      },
      solution: data?.solution || null,
    };
    if (status) {
      body.status = status;
    }
    return patchLogisticTask({ body }).catch(() => showError('Ошибка при сохранении данных.'));
  };

  const onCLickOptimize = () => {
    setWaitForStatusFlag(true);
    onCLickSave('RUNNING').catch(() => showError('Ошибка при оптимизации решения.'));
  };

  const onStopOptimizing = () => {
    setWaitForStatusFlag(true);
    stopTaskTrigger({ id: logisticTask.data.logisticTaskId }).catch(() =>
      showError('Ошибка при остановке оптимизации.'),
    );
  };

  const onCLickCreateRoutes = () => {
    setIsOpenCreateVehicleRouteDialog(true);
  };

  const handleClose = useCallback(() => {
    setIsOpenCreateVehicleRouteDialog(false);
    dispatch(logisticTasksPetrolResourceActions.setRouteNameTemplate(ROUTE_NAME_TPLS[0].value));
  }, [setIsOpenCreateVehicleRouteDialog, dispatch, logisticTasksPetrolResourceActions]);

  const processCreateRoutes = async () => {
    const { data: newData } = await onCLickSave();
    await createVehicleRoutes({
      body: newData,
      rePlanDate,
      routeNameTemplate,
      taskName,
      selectedVehicles,
    }).catch(() => showError('Ошибка при создании маршрутов.'));

    handleClose();
  };

  const showAwaitForOptimisationToast = useCallback(
    (evt) => {
      if (isRunning || isStopping) {
        toast.error('Дождитесь завершения оптимизации', {
          duration: 3000,
          style: { maxWidth: '100%' },
        });
        cancelEvent(evt);
      }
    },
    [isRunning, isStopping],
  );

  return (
    <>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={
          !!(solveLogisticTask.isLoading || resultPatchLogisticTask.isLoading || reorderLogisticTaskResult.isLoading)
        }
      >
        <BackdropWrapper>
          <CircularProgress color="inherit" />
          <Typography variant="body1">{solveLogisticTask.isLoading ? 'Оптимизация...' : 'Сохранение...'}</Typography>
        </BackdropWrapper>
      </Backdrop>
      <CreateRoutesDialog
        open={!!isOpenCreateVehicleRouteDialog}
        handleClose={handleClose}
        selectedVehicles={selectedVehicles}
        availableTankers={data?.sourceWithSolution?.availableTankers}
        routeNameTemplate={routeNameTemplate}
        setRouteNameTemplate={(tpl) => dispatch(logisticTasksPetrolResourceActions.setRouteNameTemplate(tpl))}
        processCreateRoutes={processCreateRoutes}
        loading={createVehicleRoutesResult.isLoading || resultPatchLogisticTask.isLoading}
      />
      <MainPanelContent
        isRunning={isRunning}
        isStopping={isStopping}
        showAwaitForOptimisationToast={showAwaitForOptimisationToast}
        logisticTask={logisticTask}
        reorderLogisticTask={reorderLogisticTask}
        waitForStatusFlag={waitForStatusFlag}
        onCLickSave={onCLickSave}
        onCLickOptimize={onCLickOptimize}
        onStopOptimizing={onStopOptimizing}
        onCLickCreateRoutes={onCLickCreateRoutes}
      />
    </>
  );
}

PetrolDeliveryMainPanelBll.propTypes = {
  logisticTask: PropTypes.shape({
    data: PropTypes.shape(),
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    refetch: PropTypes.func,
  }),
  taskStatus: PropTypes.shape({
    status: PropTypes.oneOf(['CREATED', 'RUNNING', 'SOLVED', 'PAUSE', 'STOPPED', 'STOPPING']),
  }),
  patchLogisticTask: PropTypes.func,
  startPolling: PropTypes.func,
  stopPolling: PropTypes.func,
  solveLogisticTask: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
  resultPatchLogisticTask: PropTypes.shape({
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
  }),
  stopLogisticTask: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({
        isLoading: PropTypes.bool,
        isSuccess: PropTypes.bool,
      }),
    ]),
  ),
};

PetrolDeliveryMainPanelBll.defaultProps = {
  logisticTask: null,
  taskStatus: { status: null },
  solveLogisticTask: null,
  resultPatchLogisticTask: null,
  patchLogisticTask: () => {},
  startPolling: () => {},
  stopPolling: () => {},
  stopLogisticTask: null,
};
